import { useState } from 'react';
import { displayNum } from './numFormat';

// Controlled numeric input with local draft state.
//
// The store holds tolerances as Numbers; in-flight typing (e.g. `0,`
// while the user is mid-decimal) doesn't survive a parse-render round
// trip, so each input carries its own draft string. On blur / Enter
// the draft is parsed and pushed to the store via `onCommit(value)`.
// External value changes (different row id, store-side updates) reset
// the draft via the render-time prop-watch pattern below — no
// useEffect needed, per the React 19 "adjusting state on prop change"
// guidance (https://react.dev/reference/react/useState#storing-information-from-previous-renders).

export default function NumInput({
  value,
  onCommit,
  placeholder,
  align = 'right',
  ghost = false,
  className = '',
  style = {},
  inputRef,
  ariaLabel,
  onEnter,
  title,
}) {
  const [draft, setDraft] = useState(() => displayNum(value));
  const [lastValue, setLastValue] = useState(value);
  if (value !== lastValue) {
    setLastValue(value);
    setDraft(displayNum(value));
  }

  // onCommit kann 'reset' oder 'reject' liefern: der Text wurde anders
  // verarbeitet (Toleranzklasse, C6), das Feld zeigt wieder den Wert der
  // Zeile. Bei 'reject' wurde nichts übernommen; Enter legt dann keine
  // neue Zeile an. Rückgabe: true, wenn Enter weitergehen darf.
  const commit = () => {
    const r = onCommit?.(draft);
    if (r === 'reset' || r === 'reject') setDraft(displayNum(value));
    return r !== 'reject';
  };

  return (
    <input
      ref={inputRef}
      value={draft}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value.replace('.', ','))}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (commit()) onEnter?.();
        }
      }}
      inputMode="decimal"
      aria-label={ariaLabel}
      title={title}
      className={`num focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-brand)]/40 focus-visible:bg-brand-soft/40 ${className}`}
      style={{
        width: '100%', height: '100%',
        border: 'none', outline: 'none',
        background: 'transparent',
        textAlign: align,
        fontSize: 13,
        color: ghost ? 'var(--color-ink-4)' : 'var(--color-ink)',
        padding: 0,
        ...style,
      }}
    />
  );
}
